import { Countries } from '@xoda/common'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { Api } from '../../api'
import { formatMobile } from '../../helpers/others'
import { useCountdown } from '../../hooks/countdown'
import { useGet } from '../../hooks/get'
import { useSwitch } from '../../hooks/switch'
import { useTc } from '../../hooks/tc'
import { useUser } from '../../hooks/user'
import { ReactComponent as ArrowLeft1 } from '../../icons/arrow-left-1.svg'
import { configState } from '../../store/config'
import { Amount } from '../Amount'
import { Button } from '../Button'
import { Field } from '../Field'
import { Fields, Form, FormProps } from '../Form'
import { getGoodsType, Goods, GoodsItem, mapPacks, mapPlans } from '../Goods'
import { Input } from '../Input'
import { Loading } from '../Loading'
import { Modal, ModalRef } from '../Modal'
import { PaymentMethodForm } from '../PaymentMethodForm'
import { PromoCodeField } from '../PromoCodeField'
import { Select } from '../Select'
import { BankAccountFieldsProps } from './BankAccountFields'
import { CreditCardFieldsProps } from './CreditCardFields'
import { SignModalsContext } from './SignModals'
import s from './SignUpModal.module.css'

const SignUpModal: FC<SignUpModalProps> = (props) => {
  const context = useContext(SignModalsContext)

  const { onFinish, onCancel } = props

  const config = useRecoilValue(configState)

  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: apiPacks } = useGet<Dto.Pack[]>([
    'member_portal/packs',
    { ...(context.categoryIds && { category_id: context.categoryIds }) },
  ])
  const { data: apiPlans } = useGet<Dto.Plan[]>([
    'member_portal/plans',
    { ...(context.categoryIds && { category_id: context.categoryIds }) },
  ])

  const { signIn } = useUser()
  const {
    value: codeCountdownValue,
    start: starCodeCountdown,
    running: codeCountdownRunning,
  } = useCountdown()
  const { on: opened, close } = useSwitch(true)
  const { on: pending, open: startPending, close: stopPending } = useSwitch()

  const [step, setStep] = useState(0)
  const [lead, setLead] = useState<Dto.Lead>()
  const [selectedPackage, setSelectedPackage] = useState<GoodsItem>()
  const [promoCode, setPromoCode] = useState<string>()

  const packages = useMemo(() => {
    const packs = apiPacks && mapPacks(apiPacks)
    const plans = apiPlans && mapPlans(apiPlans)
    return !packs || !plans ? packs || plans : [...packs, ...plans]
  }, [apiPacks, apiPlans])

  const baseParams = useMemo(() => {
    return new URLSearchParams({
      lead_id: String(lead?.id),
      ...(context.purchase
        ? {
            [`${getGoodsType(context.purchase.item)}_id`]:
              context.purchase.item.id,
            ...(context.purchase.promoCode && {
              promo_code: context.purchase.promoCode,
            }),
          }
        : selectedPackage
        ? {
            [`${selectedPackage.type}_id`]: String(selectedPackage.key),
            ...(promoCode && {
              promo_code: promoCode,
            }),
          }
        : undefined),
    })
  }, [context.purchase, lead?.id, promoCode, selectedPackage])

  const { data: checkout, isValidating: checkoutLoading } =
    useGet<Dto.Checkout>(() => {
      if (!lead) {
        return
      }

      return [
        'member_portal/checkout',
        {
          lead_id: lead.id,
          success_url: String(
            new URL(`/stripe?${baseParams}`, window.location.origin)
          ),
          cancel_url: String(new URL('/classes', window.location.origin)),
        },
      ]
    })

  const { tcElement, paymentTcElement, formTc, gymTc } = useTc(
    selectedPackage?.dto,
    checkout
  )

  const personalFields = useRef<Fields>()
  const sendsCodeBySms = useRef(false)
  const codeId = useRef<number>()
  const modal = useRef<ModalRef>(null)

  const steps = [
    t('Personal Details'),
    t('Verify Identity'),
    t('Packages'),
    t('Payment'),
    '',
  ]
  const redirectingPayment =
    checkout && ['stripe', 'paychoice'].includes(checkout.payment.method)

  const packageFree = selectedPackage
    ? !selectedPackage.price ||
      (selectedPackage.type === 'plan' &&
        selectedPackage.dto.is_trial &&
        !selectedPackage.dto.details?.payment.member_fee.amount)
    : false

  const handleRequestClose = useCallback(() => {
    close()
    onCancel?.()
  }, [close, onCancel])

  const sendCode = useCallback(async () => {
    if (!personalFields.current) {
      return
    }

    codeId.current = (
      await Api.post(
        'verification/codes',
        sendsCodeBySms.current
          ? {
              type: 'sms',
              receiver: formatMobile(
                personalFields.current.countryCode,
                personalFields.current.mobile
              ),
            }
          : {
              type: 'email',
              receiver: personalFields.current.email,
            }
      )
    ).id
    starCodeCountdown()
  }, [starCodeCountdown])

  const go = useCallback((offset = 1) => {
    setStep((prev) => prev + offset)
    modal.current?.toTop()
  }, [])

  const handleBackClick = useCallback(() => {
    if (step === 2) {
      go(-2)
      return
    }

    go(-1)
  }, [go, step])

  const handlePersonSubmit: FormProps['onSubmit'] = useCallback(
    async (fields) => {
      try {
        startPending()
        setLead(
          await Api.post('member_portal/leads', {
            first_name: fields.firstName,
            last_name: fields.lastName,
            email: fields.email,
            mobile: formatMobile(fields.countryCode, fields.mobile),
            source: fields.source,
          })
        )
        personalFields.current = fields

        if (fields.mobile) {
          sendsCodeBySms.current = window.confirm(
            t('Do you want to receive the verification code via SMS?')
          )
        }

        await sendCode()
        go()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [go, sendCode, startPending, stopPending, t]
  )

  const handleVerifySubmit: FormProps['onSubmit'] = useCallback(
    async (fields) => {
      startPending()

      try {
        await Api.post('verification/check', {
          id: codeId.current,
          code: fields.code,
        })
        go()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [go, startPending, stopPending]
  )

  const handlePackagesNextClick: FormProps['onSubmit'] = useCallback(
    (fields) => {
      setPromoCode(fields['promo-code'])
      go()
    },
    [go]
  )

  const handlePaySucceed: (
    | CreditCardFieldsProps
    | BankAccountFieldsProps
  )['onSucceed'] = useCallback(
    async (payment) => {
      try {
        startPending()
        const body = await Api.post('member_portal/members', {
          lead_id: lead!.id,
          payment,
          ...(formTc
            ? { terms: { form_id: formTc.id, version: formTc.version } }
            : gymTc
            ? { terms: { id: gymTc.id } }
            : undefined),
          ...(context.purchase
            ? {
                [`${getGoodsType(context.purchase.item)}_id`]:
                  context.purchase.item.id,
                promo_code: context.purchase.promoCode,
              }
            : selectedPackage
            ? {
                [`${selectedPackage.type}_id`]: selectedPackage.key,
                promo_code: promoCode,
              }
            : undefined),
        })
        await signIn({
          memberId: body.member_id,
          leadId: body.lead_id,
          userId: body.user_id,
          email: personalFields.current!.email,
          token: body.token,
          firstName: body.first_name,
          lastName: body.last_name,
          gymId: Number(body.gym_id),
        })
        go()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [
      context.purchase,
      formTc,
      go,
      gymTc,
      lead,
      promoCode,
      selectedPackage,
      signIn,
      startPending,
      stopPending,
    ]
  )

  const handleFinishClick = useCallback(() => {
    close()
    onFinish?.()
  }, [close, onFinish])

  useEffect(() => {
    if (step !== 3 || pending) {
      return
    }

    if (packageFree) {
      handlePaySucceed({ free: true })
      return
    }

    if (!checkout || !redirectingPayment) {
      return
    }

    if (checkout.payment.redirect_url) {
      window.location.assign(checkout.payment.redirect_url)
      return
    }

    if (checkout.payment.method === 'stripe') {
      const params = new URLSearchParams({
        ...Object.fromEntries(baseParams),
        connected_account_id: checkout.payment.stripe_connected_account_id!,
        client_secret: checkout.payment.client_secret!,
        customer_id: checkout.payment.stripe_customer_id!,
      })

      navigate(`/stripe?${params}`)
      return
    }

    if (checkout.payment.method === 'paychoice') {
      const params = new URLSearchParams({
        ...Object.fromEntries(baseParams),
        private_key: checkout.payment.paychoice_api_key!,
        username: checkout.payment.paychoice_account_guid!,
        customer_id: checkout.payment.paychoice_customer_id!,
        customer_reference: checkout.payment.customer_reference!,
      })

      navigate(`/paychoice?${params}`, {})
      return
    }
  }, [
    checkout,
    handlePaySucceed,
    navigate,
    packageFree,
    step,
    baseParams,
    redirectingPayment,
    pending,
  ])

  return (
    <>
      <Modal
        open={opened}
        closesWithConfirm
        onClose={handleRequestClose}
        ref={modal}
      >
        <div className={s.container}>
          <header className={s.header}>
            {step > 0 && step < steps.length - 1 && (
              <Button variant="text" onClick={handleBackClick}>
                <ArrowLeft1 />
              </Button>
            )}
            <h1 className={s.title}>{steps[step]}</h1>
          </header>
          {step === 0 && (
            <Form onSubmit={handlePersonSubmit}>
              <Field
                label={t('First Name')}
                control={
                  <Input
                    name="firstName"
                    required
                    block
                    autoFocus
                    defaultValue={personalFields.current?.firstName}
                  />
                }
              />
              <Field
                label={t('Last Name')}
                control={
                  <Input
                    name="lastName"
                    required
                    block
                    defaultValue={personalFields.current?.lastName}
                  />
                }
              />
              <Field
                label={t('Email')}
                control={
                  <Input
                    type="email"
                    name="email"
                    required
                    block
                    defaultValue={personalFields.current?.email}
                  />
                }
              />
              <Field
                label={t('Mobile')}
                control={
                  <>
                    <Select
                      name="countryCode"
                      options={Countries.raw.map((country) => ({
                        label: `${country.code} +${country.areaCode}`,
                        value: country.areaCode,
                      }))}
                      required
                      defaultValue={personalFields.current?.countryCode}
                    />
                    <Input
                      type="tel"
                      name="mobile"
                      block
                      defaultValue={personalFields.current?.mobile}
                    />
                  </>
                }
              />
              {config?.sourceOptions && (
                <Field
                  label={t('How Do You Know About Us?')}
                  control={
                    <Select
                      name="source"
                      options={config.sourceOptions.map((option) => ({
                        value: option,
                      }))}
                      block
                      required
                      defaultValue={personalFields.current?.source}
                    />
                  }
                />
              )}
              <Button type="submit" block pending={pending}>
                {t('Next')}
              </Button>
            </Form>
          )}
          {step === 1 && (
            <Form onSubmit={handleVerifySubmit}>
              <Field
                label={t('Verification Code')}
                control={
                  <>
                    <Input
                      name="code"
                      required
                      pattern="\d{6}"
                      autoFocus
                      autoComplete="off"
                    />
                    {codeCountdownRunning ? (
                      <Button disabled>{codeCountdownValue}</Button>
                    ) : (
                      <Button onClick={sendCode}>{t('Re-send')}</Button>
                    )}
                  </>
                }
              />
              <Button type="submit" block pending={pending}>
                {t('Next')}
              </Button>
            </Form>
          )}
          {step === 2 && (
            <>
              {context.purchase ? (
                <p className={s['skips-purchase-tip']}>
                  {t(
                    'Please just skip this step because you are in a purchase process.'
                  )}
                </p>
              ) : (
                packages && (
                  <Goods
                    items={packages}
                    defaultSelected={selectedPackage?.key}
                    onSelect={setSelectedPackage}
                  />
                )
              )}
              <Form onSubmit={handlePackagesNextClick}>
                {!context.purchase && <PromoCodeField />}
                <Button
                  type="submit"
                  block
                  disabled={
                    (!context.purchase && !selectedPackage) || checkoutLoading
                  }
                >
                  {t('Next')}
                </Button>
              </Form>
              {!context.purchase && (
                <Amount
                  {...(selectedPackage?.type === 'plan' &&
                  selectedPackage.joinFee
                    ? {
                        items: [
                          { name: t('Price'), price: selectedPackage.price },
                          {
                            name: t('Join fee'),
                            price: selectedPackage.joinFee,
                          },
                        ],
                      }
                    : {
                        total: selectedPackage?.price || 0,
                      })}
                />
              )}
            </>
          )}
          {step === 3 &&
            (personalFields.current &&
            checkout &&
            !redirectingPayment &&
            !packageFree ? (
              <>
                <PaymentMethodForm
                  lastName={personalFields.current.lastName}
                  payment={checkout.payment}
                  action={t('Pay Now')}
                  pending={pending}
                  tcElement={
                    <>
                      {tcElement}
                      {paymentTcElement}
                    </>
                  }
                  onSubmit={startPending}
                  onSucceed={handlePaySucceed}
                  onError={stopPending}
                />
                {!context.purchase && (
                  <Amount
                    items={[selectedPackage!].map((pack) => ({
                      name:
                        pack.type === 'pack'
                          ? `${pack.name} ×${t('classWithCount', {
                              count: pack.classesNumber,
                            })}`
                          : t('Unlimited'),
                      price: pack.price,
                    }))}
                  />
                )}
                {checkout?.payment.fees && (
                  <div className={s.footnote}>
                    &#9432; Payment will incur setup fee (
                    {checkout.payment.fees.load_fee}) and transaction fee (
                    {checkout.payment.fees.transaction_fee
                      .map(({ type, value }) => `${type}: ${value}`)
                      .join(', ')}
                    ).
                  </div>
                )}
              </>
            ) : (
              <Loading />
            ))}
          {step === 4 && (
            <>
              <p className={s.welcome}>
                {t('Thank you for signing up with us.')}
                <br />
                {t(
                  'You will receive an email with how you can download and log into our app.'
                )}
                <br />
                {t('We will look forward to seeing you soon.')}
                <br />
                {t('Please contact us for any further assistance.')}
              </p>
              <Button block onClick={handleFinishClick}>
                {t('Finish')}
              </Button>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

type SignUpModalProps = {
  onFinish?: VoidFunction
  onCancel?: VoidFunction
}

export { SignUpModal }
export type { SignUpModalProps }
